import {
	CheckCircleOutlined,
	ExclamationCircleOutlined,
	LoadingOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { Button, Flex, Popover, Space, Table, Tooltip, Typography } from 'antd';
import Link from 'antd/es/typography/Link';

import { CopilotHistoryPageItem } from '@/api/batch_process';
import FavouriteIcon from '@/components/common/FavouriteIcon';
import InfoPopover from '@/components/common/InfoPopover';
import MultiButton from '@/components/common/MultiButton';
import prepareImageSrc from '@/lib/helpers/prepareImageSrc';
import { IHistoryItem, ISingleVideoCollectionItem } from '@/ts';

import styles from './styles.module.css';

import Platforms from '../../../creators/platforms';
import ThumbnailImage from '../../ThumbnailImage';

type TProps = {
	data: any;
	columns: any;
	onTableChange: any;
	isLoading: boolean;
	isFetching: boolean;
	pagination: any;
	setPagination: any;
};

export default function SingleCollectionVideosTable({
	data,
	columns,
	onTableChange,
	isLoading,
	isFetching,
	pagination,
	setPagination,
}: TProps) {
	return (
		<Table
			size="small"
			dataSource={data}
			// dataSource={data?.data?.data?.results}
			columns={[
				...columns,
				{
					title: 'Status / Title',
					dataIndex: 'title',
					key: 'title',
					width: 200,
					ellipsis: true,
					render: (_: any, record: CopilotHistoryPageItem) => {
						const preparedTitle =
							(record?.title || record?.description)?.length > 90
								? (record?.title || record?.description).slice(0, 90) + '...'
								: record?.title || record?.description;

						let IconComponent = null;

						switch (record?.status) {
							case 0:
							case undefined:
								IconComponent = (
									<Tooltip title={'Adding to queue...'}>
										<LoadingOutlined
											style={{ color: 'gray' }}
											// className={styles.indicatorIcon}
										/>
									</Tooltip>
								);
								break;

							case 1:
								IconComponent = (
									<Tooltip title={'Processing...'}>
										<SyncOutlined
											spin
											style={{
												color: '#172b6f',
											}}
											// className={styles.indicatorIcon}
										/>
									</Tooltip>
								);
								break;

							case 2:
								IconComponent = (
									<Tooltip title={'Analysis complete'}>
										<CheckCircleOutlined
											style={{
												color: '#58C622',
											}}
											// className={styles.indicatorIcon}
										/>
									</Tooltip>
								);
								break;

							case 3:
							case 4:
							case 5:
								IconComponent = (
									<Tooltip
										title={'We experienced an issue analysing this video.'}
									>
										<ExclamationCircleOutlined
											style={{
												color: 'orange',
											}}
											// className={styles.indicatorIcon}
										/>
									</Tooltip>
								);
								break;

							default:
								IconComponent = (
									<Tooltip title={'Adding to queue...'}>
										<LoadingOutlined
											style={{ color: 'gray' }}
											// className={styles.indicatorIcon}
										/>
									</Tooltip>
								);
								break;
						}

						return (
							<>
								<Space size={[8, 1]}>
									{/* <Avatar
						src={
							record?.thumbnail
								? prepareImageSrc(record?.thumbnail)
								: placeholderImage
						}
						alt={`${record?.platform} avatar`}
					/>{' '} */}
									{IconComponent}
									<ThumbnailImage
										src={
											record?.thumbnail
												? prepareImageSrc(record.thumbnail)
												: undefined
										}
										placeholderImage={''}
									/>
									<Link href={record?.url} ellipsis target="_blank">
										{preparedTitle ||
											record?.url?.replace('https://www.', '') ||
											''}
									</Link>
								</Space>
								{record?.favourite && <FavouriteIcon />}
							</>
						);
					},
				},
				{
					title: 'Platform / Creator',
					dataIndex: 'platform',
					key: 'platform',
					// sorter: true,
					elipsis: true,
					width: 60,
					// flex: true,
					// hidden: true,
					render: (_: any, record: CopilotHistoryPageItem) => {
						return (
							<Flex justify="flex-start" align="center" gap={8}>
								<Platforms platform={record?.platform} />

								{record?.creatorUrls?.[0] ? (
									<Button
										type="link"
										href={record?.creatorUrls?.[0]}
										target="_blank"
										style={{
											width: 'fit-content',
											display: 'flex',
											wordWrap: 'break-word',
											overflow: 'hidden',
											padding: 0,
										}}
									>
										<span
											style={{
												width: '100%',
												display: 'inline-block',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											}}
										>
											{record?.creatorName}
										</span>
									</Button>
								) : (
									<>
										{record?.creatorName ? (
											<Typography.Text
												style={{
													width: 'auto',
													display: 'inline-block',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}
											>
												{record?.creatorName}
											</Typography.Text>
										) : null}
									</>
								)}
							</Flex>
						);
					},
				},
				{
					title: 'Actions',
					// ellipsis: true,
					width: 65,
					dataIndex: 'actions',
					key: 'actions',
					fixed: 'right',
					render: (_: any, record: ISingleVideoCollectionItem) => {
						return (
							<Flex justify="center" align="center" style={{ gap: '4px' }}>
								<MultiButton
									record={record}
									keys={['view', 'share', 'favourite', 'delete']}
								/>

								{/* <HistoryItemButton
                  url={record.url}
                  setDrawerOpen={setOpenDrawer}
                />
                <ShareButton
                  url={record?.url}
                  disabled={record?.status !== 2}
                /> */}
								{/* <SwitchFavourites
                  isFavorite={record.favourite}
                  id={record.id}
                /> */}

								{/* <Popconfirm
                  title={`Sure to remove video from collection?`}
                  onConfirm={() =>
                    onDeleteClick(
                      record?.user_video_collection_id,
                      record?.user_video_collection_item_id
                    )
                  }
                >
                  <Tooltip title="Remove">
                    <DeleteVideoButton
                      title={"Remove"}
                      onClick={() => {}}
                      // loading={isDeleteLoading}
                      // disabled={isDeleteLoading}
                    />
                  </Tooltip>
                </Popconfirm> */}
							</Flex>
						);
					},
				},
			]}
			style={{ width: '100%' }}
			rowClassName="editable-row"
			rowKey={'user_video_collection_item_id'}
			onChange={onTableChange}
			loading={isLoading || isFetching}
			bordered
			pagination={{
				total: pagination?.total || 1,
				showSizeChanger: true,
				current: pagination?.page || 1,
				pageSizeOptions: [10, 25, 50],
				onChange: (page: number, pageSize: number) => {
					setPagination((cur: any) => ({ ...cur, page, limit: pageSize }));
				},
			}}
		/>
	);
}
